import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SectionHeader from "../common/SectionHeader";
import AccentCard from "../common/AccentCard";

const toComparable = (value) => {
  if (value == null) return "";
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  return String(value).toLowerCase();
};

const CommonDataGrid = ({
  rows,
  columns,
  getRowId,
  onRowClick,
  title = "All Assets",
  headerAction = null,
  initialSortField,
  initialSortDirection = "desc",
  filterPlaceholder = "Filter rows...",
  dense = false,
  outlined = true,
}) => {
  const defaultSortField =
    initialSortField || columns.find((c) => c.sortable !== false)?.field;
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);
  const [filterValue, setFilterValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const filteredRows = useMemo(() => {
    const needle = filterValue.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((row) =>
      columns.some((col) => {
        const value = col.valueGetter ? col.valueGetter(row) : row[col.field];
        return String(value ?? "").toLowerCase().includes(needle);
      })
    );
  }, [rows, columns, filterValue]);

  const sortedRows = useMemo(() => {
    if (!sortField) return filteredRows;
    const col = columns.find((c) => c.field === sortField);
    if (!col) return filteredRows;

    const sortFactor = sortDirection === "asc" ? 1 : -1;

    return [...filteredRows].sort((a, b) => {
      const aValue = col.valueGetter ? col.valueGetter(a) : a[sortField];
      const bValue = col.valueGetter ? col.valueGetter(b) : b[sortField];

      if (col.sortComparator) {
        return col.sortComparator(aValue, bValue, a, b) * sortFactor;
      }

      const aa = toComparable(aValue);
      const bb = toComparable(bValue);
      if (aa < bb) return -1 * sortFactor;
      if (aa > bb) return 1 * sortFactor;
      return 0;
    });
  }, [filteredRows, columns, sortField, sortDirection]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDirection("asc");
  };

  return (
    <AccentCard
      variant={outlined ? "outlined" : undefined}
      sx={{ borderRadius: 2, minWidth: 0, maxWidth: "100%", boxSizing: "border-box" }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <SectionHeader variant="h6" sx={{ fontSize: "1rem" }}>
          {title}
        </SectionHeader>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {headerAction}
          <TextField
            size="small"
            value={filterValue}
            onChange={(e) => {
              setFilterValue(e.target.value);
              setPage(0);
            }}
            placeholder={filterPlaceholder}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table size={dense ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  align={col.align || "left"}
                  sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
                >
                  {col.sortable === false ? (
                    col.headerName
                  ) : (
                    <TableSortLabel
                      active={sortField === col.field}
                      direction={sortField === col.field ? sortDirection : "asc"}
                      onClick={() => handleSort(col.field)}
                    >
                      {col.headerName}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((row) => {
              const rowId = getRowId(row);
              return (
                <TableRow
                  hover
                  key={rowId}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{ cursor: onRowClick ? "pointer" : "default" }}
                >
                  {columns.map((col) => (
                    <TableCell key={`${rowId}-${col.field}`} align={col.align || "left"}>
                      {col.renderCell
                        ? col.renderCell(row)
                        : col.valueFormatter
                        ? col.valueFormatter(col.valueGetter ? col.valueGetter(row) : row[col.field], row)
                        : col.valueGetter
                        ? col.valueGetter(row)
                        : row[col.field]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {pagedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No matching assets.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sortedRows.length}
        page={page}
        onPageChange={(_, value) => setPage(value)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </AccentCard>
  );
};

CommonDataGrid.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  getRowId: PropTypes.func,
  onRowClick: PropTypes.func,
  title: PropTypes.string,
  headerAction: PropTypes.node,
  initialSortField: PropTypes.string,
  initialSortDirection: PropTypes.oneOf(["asc", "desc"]),
  filterPlaceholder: PropTypes.string,
  dense: PropTypes.bool,
  outlined: PropTypes.bool,
};

CommonDataGrid.defaultProps = {
  getRowId: (row) => row.id,
  dense: false,
};

export default CommonDataGrid;
